/**
 * Utilitários para manipulação de JWT
 */

/**
 * Decodifica um token JWT sem verificar assinatura
 * @param {string} token - Token JWT
 * @returns {Object|null} - Payload decodificado ou null se inválido
 */
export const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decodifica o payload (segunda parte)
    const payload = parts[1];
    
    // Adiciona padding se necessário para base64
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    // Decodifica base64
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Erro ao decodificar JWT:', error);
    return null;
  }
};

/**
 * Verifica se um token JWT está expirado
 * @param {string} token - Token JWT
 * @returns {boolean} - true se expirado, false caso contrário
 */
export const isTokenExpired = (token) => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Considera expirado se não tiver exp
  }

  // exp está em segundos, Date.now() está em milissegundos
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return currentTime >= expirationTime;
};

/**
 * Obtém o tempo restante até a expiração do token em milissegundos
 * @param {string} token - Token JWT
 * @returns {number|null} - Tempo restante em ms ou null se inválido
 */
export const getTokenTimeRemaining = (token) => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const remaining = expirationTime - currentTime;

  return remaining > 0 ? remaining : 0;
};

/**
 * Verifica se o token está próximo de expirar
 * @param {string} token - Token JWT
 * @param {number} thresholdMinutes - Minutos antes da expiração para considerar "próximo" (padrão: 5)
 * @returns {boolean} - true se está próximo de expirar
 */
export const isTokenExpiringSoon = (token, thresholdMinutes = 5) => {
  const timeRemaining = getTokenTimeRemaining(token);
  if (timeRemaining === null) {
    return true; // Considera expirando se não conseguir verificar
  }

  const thresholdMs = thresholdMinutes * 60 * 1000;
  return timeRemaining <= thresholdMs;
};

/**
 * Obtém a data de expiração do token
 * @param {string} token - Token JWT
 * @returns {Date|null} - Data de expiração ou null se inválido
 */
export const getTokenExpirationDate = (token) => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return new Date(payload.exp * 1000);
};
